import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import {
	NavLink
} from 'react-router-dom';
import { mergeSelectStyles } from '../../../theme.js';

const permissionSelectStyles = mergeSelectStyles({
	container: (provided) => ({
		...provided,
		minWidth: 0,
		width: '100%'
	})
});

const UserList = ({ accounts, changeLevel, changeNeedsToPunchInOut, getUsers, permissions }) => {
	const [punchOverrides, setPunchOverrides] = useState({});
	const [searchTerm, setSearchTerm] = useState('');
	const [showInactive, setShowInactive] = useState(false);
	const users = getUsers() || [];
	const accountsByFirebaseId = useMemo(() => (
		(accounts || []).reduce((byFirebaseId, account) => {
			byFirebaseId[account.id_firebase] = account;
			return byFirebaseId;
		}, {})
	), [accounts]);
	const permissionOptions = useMemo(() => (
		(permissions || []).map(permission => ({ value: permission.uid, label: permission.name }))
	), [permissions]);
	const isActive = (account) => !account || account.is_active === undefined || account.is_active === null || Number(account.is_active) !== 0;
	const inactiveCount = users.filter(user => !isActive(accountsByFirebaseId[user.uid])).length;
	const filteredUsers = useMemo(() => {
		const query = searchTerm.trim().toLowerCase();
		return users
			.filter(user => showInactive ? !isActive(accountsByFirebaseId[user.uid]) : isActive(accountsByFirebaseId[user.uid]))
			.filter(user => {
				if (!query) return true;
				const searchableText = [user.email, user.name, user.displayName, user.firstName, user.lastName, user.uid]
					.filter(Boolean).join(' ').toLowerCase();
				return searchableText.includes(query);
			});
	}, [accountsByFirebaseId, searchTerm, showInactive, users]);

	const changePunchRequirement = (uid, checked) => {
		setPunchOverrides(current => ({ ...current, [uid]: checked ? 1 : 0 }));
		changeNeedsToPunchInOut(uid, checked ? 1 : 0);
	};

	return (
		<main className="main ctnPage user-management">
			<header className="user-management__header">
				<div>
					<p className="user-management__eyebrow">Administration</p>
					<h1 className="user-management__title">User management</h1>
					<p className="user-management__description">Review access and configure punch-clock requirements for each user.</p>
				</div>
				<div className="user-management__count" aria-label={`${filteredUsers.length} of ${users.length} users shown`}>
					<strong>{filteredUsers.length === users.length ? users.length : `${filteredUsers.length}/${users.length}`}</strong>
					<span>{users.length === 1 ? 'user' : 'users'}</span>
				</div>
			</header>

			<section className="user-management__table-card" aria-label="Users">
				<div className="user-management__toolbar">
					<div className="user-management__toolbar-heading">
						<label className="user-management__search-label" htmlFor="user-management-search">Find a user</label>
						<span className="user-management__scope">{showInactive ? 'Inactive accounts only' : 'Active accounts only'}</span>
					</div>
					<div className="user-management__search-wrap">
						<i className="bi bi-search user-management__search-icon" aria-hidden="true" />
						<input
							id="user-management-search"
							className="user-management__search"
							type="search"
							value={searchTerm}
							onChange={event => setSearchTerm(event.target.value)}
							placeholder="Search by email or name..."
							aria-label="Search users by email or name"
						/>
						{searchTerm && (
							<button className="user-management__search-clear" type="button" onClick={() => setSearchTerm('')} aria-label="Clear user search">&times;</button>
						)}
					</div>
					<button className="user-management__inactive-button btn btn-sm" type="button" onClick={() => setShowInactive(current => !current)} disabled={!showInactive && inactiveCount === 0}>
						<i className={`bi ${showInactive ? 'bi-eye-slash' : 'bi-archive'} mr-1`} aria-hidden="true"></i>
						{showInactive ? 'Show active' : `Show inactive${inactiveCount ? ` (${inactiveCount})` : ''}`}
					</button>
				</div>
				{users.length === 0 || filteredUsers.length === 0 ? (
					<div className="user-management__empty">{users.length === 0 ? 'No users are available yet.' : searchTerm ? `No users match “${searchTerm}”.` : `No ${showInactive ? 'inactive' : 'active'} accounts are available.`}</div>
				) : (
					<div className="table-responsive">
						<table className="table user-management__table mb-0">
							<thead>
								<tr>
									<th className="user-management__number" scope="col">#</th>
									<th scope="col">User ID</th>
									<th scope="col">Email</th>
									<th className="user-management__punch-column" scope="col" title="Require this user to punch in and out">Punch clock</th>
									<th scope="col">Last sign-in</th>
									<th className="user-management__access-column" scope="col">Access</th>
								</tr>
							</thead>
							<tbody>
								{filteredUsers.map((user, index) => {
									const account = accountsByFirebaseId[user.uid];
									const needsToPunchIn = punchOverrides[user.uid] ?? account?.need_to_punch_in ?? 0;
									const selectedPermissions = (user.permissions || []).map(permissionUid => {
										const permission = permissionOptions.find(option => option.value === permissionUid);
										return permission || null;
									}).filter(Boolean);

									return (
										<tr key={user.uid}>
											<th className="user-management__number" scope="row">{index + 1}</th>
													<td data-label="User ID">
														<NavLink className="user-management__id" to={`/Account/${user.uid}`} activeClassName="active">{user.uid}</NavLink>
														{!isActive(account) && <span className="user-management__status">Inactive</span>}
											</td>
											<td data-label="Email" className="user-management__email">{user.email || '—'}</td>
											<td data-label="Punch clock" className="user-management__punch-column">
												<label className="user-management__punch-toggle">
													<input
														type="checkbox"
														checked={Boolean(needsToPunchIn)}
														onChange={(event) => changePunchRequirement(user.uid, event.target.checked)}
														aria-label={`Require punch in and out for ${user.email || user.uid}`}
													/>
													<span className="user-management__punch-switch" aria-hidden="true" />
													<span className="user-management__punch-status">{needsToPunchIn ? 'Required' : 'Not required'}</span>
												</label>
											</td>
											<td data-label="Last sign-in" className="user-management__last-sign-in">{user.lastSignInTime || 'Never'}</td>
											<td data-label="Access" className="user-management__access-column">
												<Select
													onChange={(selectedOptions) => changeLevel(user.uid, selectedOptions || [])}
													options={permissionOptions}
													value={selectedPermissions}
													isSearchable={true}
													isMulti={true}
													styles={{
														...permissionSelectStyles,
														option: (base, { data }) => ({
															...base,
															color: data.value.indexOf('write') !== -1 || data.value.indexOf('delete') !== -1 ? '#c53030' : base.color
														})
													}}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
};

export default UserList;
